import { OvButtonGroup, OvFormInput, OvFormProvider } from '@components'
import { LABELS, TOOLTIPS } from '@constants'
import { useStore } from '@hooks/useStore'
import { Close, Save, RestartAlt } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { EmailTemplateType, IEmailTemplateCreateRequest } from '@typings'
import { observer } from 'mobx-react-lite'
import { useEffect, useState, useRef } from 'react'
import { useForm } from 'react-hook-form'

interface EmailTemplateFormProps {
  open: boolean
  onClose: () => void
  templateId?: number
  initialType?: EmailTemplateType
  onSuccess?: () => void | Promise<void>
}

interface EmailTemplateFormData {
  name: string
  type: EmailTemplateType
  subject: string
  body: string
  is_default: boolean
}

const TEMPLATE_TYPE_OPTIONS = [
  { value: 'invoice', label: 'Factuur' },
  { value: 'quotation', label: 'Offerte' },
  { value: 'reminder', label: 'Herinnering' },
  { value: 'general', label: 'Algemeen' },
]

const AVAILABLE_VARIABLES = [
  { variable: '{{client_name}}', description: 'Naam van de klant' },
  { variable: '{{client_email}}', description: 'E-mailadres van de klant' },
  { variable: '{{document_number}}', description: 'Documentnummer (factuur/offerte)' },
  { variable: '{{document_date}}', description: 'Datum van het document' },
  { variable: '{{document_subject}}', description: 'Onderwerp van het document' },
  { variable: '{{document_total}}', description: 'Totaalbedrag van het document' },
  { variable: '{{company_name}}', description: 'Naam van uw bedrijf' },
  { variable: '{{company_email}}', description: 'E-mailadres van uw bedrijf' },
  { variable: '{{company_phone}}', description: 'Telefoonnummer van uw bedrijf' },
]

export const EmailTemplateForm = observer((
  { open, onClose, templateId, initialType, onSuccess }: EmailTemplateFormProps
) => {
  const { email, ui, company } = useStore()
  const [activeField, setActiveField] = useState<'subject' | 'body' | null>(null)
  const isEditMode = !!templateId
  const subjectInputRef = useRef<HTMLInputElement>(null)
  const bodyInputRef = useRef<HTMLTextAreaElement>(null)

  const form = useForm<EmailTemplateFormData>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      type: initialType || 'invoice',
      subject: '',
      body: '',
      is_default: false,
    },
  })

  // Load template data when editing
  useEffect(() => {
    if (open && templateId) {
      const loadTemplate = async () => {
        try {
          const template = await email.getTemplate(templateId)
          form.reset({
            name: template.name,
            type: template.type,
            subject: template.subject,
            body: template.body,
            is_default: template.is_default,
          })
        } catch (error) {
          console.error('Error loading template:', error)
          ui.handleToaster('Fout bij het laden van het sjabloon', { type: 'error' })
        }
      }
      loadTemplate()
    } else if (open && !templateId) {
      // Reset form for create mode
      form.reset({
        name: '',
        type: initialType || 'invoice',
        subject: '',
        body: '',
        is_default: false,
      })
    }
  }, [open, templateId, initialType])

  const handleClose = () => {
    form.reset()
    setActiveField(null)
    onClose()
  }

  const insertVariable = (variable: string) => {
    const field = activeField || 'body'
    const currentValue = form.getValues(field)
    const inputRef = field === 'subject' ? subjectInputRef : bodyInputRef
    const input = inputRef.current

    if (input) {
      const start = input.selectionStart || currentValue.length
      const end = input.selectionEnd || currentValue.length
      const newValue = currentValue.substring(0, start) + variable + currentValue.substring(end)

      form.setValue(field, newValue, { shouldValidate: true, shouldDirty: true })

      // Set cursor position after inserted variable
      setTimeout(() => {
        const newPosition = start + variable.length
        input.setSelectionRange(newPosition, newPosition)
        input.focus()
      }, 0)
    } else {
      // Fallback: append to end
      form.setValue(field, currentValue + variable, { shouldValidate: true, shouldDirty: true })
    }
  }

  const handleRestoreToDefault = async () => {
    try {
      await ui.confirm({
        title: 'Sjabloon herstellen naar standaardwaarden?',
        content:
          'Dit zal alle huidige wijzigingen vervangen door de standaard sjablooninhoud. ' +
          'Deze actie kan niet ongedaan worden gemaakt.',
        withCancel: true,
      })

      const currentType = form.getValues('type')
      const defaultTemplate = await email.getDefaultTemplate(currentType)

      // Update form with default values
      form.setValue('name', defaultTemplate.name, { shouldValidate: true, shouldDirty: true })
      form.setValue('subject', defaultTemplate.subject, { shouldValidate: true, shouldDirty: true })
      form.setValue('body', defaultTemplate.body, { shouldValidate: true, shouldDirty: true })

      ui.handleToaster('Sjabloon hersteld naar standaardwaarden', { type: 'success' })
    } catch (error: any) {
      // User cancelled or error occurred
      if (error?.message) {
        ui.handleToaster('Fout bij het herstellen van het sjabloon', { type: 'error' })
      }
    }
  }

  const onSubmit = async (data: EmailTemplateFormData) => {
    try {
      if (isEditMode) {
        await email.updateTemplate(templateId, data)
        ui.handleToaster('Sjabloon bijgewerkt', { type: 'success' })
      } else {
        const createData: IEmailTemplateCreateRequest = {
          ...data,
          company_id: company.current_company?.id || 0,
        }
        await email.createTemplate(createData)
        ui.handleToaster('Sjabloon aangemaakt', { type: 'success' })
      }

      // Call onSuccess callback to refresh list
      if (onSuccess) {
        await onSuccess()
      }

      handleClose()
    } catch (error: any) {
      console.error('Error saving template:', error)
      const errorMessage = error?.message || 'Er is een fout opgetreden bij het opslaan van het sjabloon'
      ui.handleToaster(errorMessage, { type: 'error' })
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          maxHeight: '90vh',
        },
      }}>
      <OvFormProvider
        form={form}
        readOnly={false}
        onSubmit={onSubmit as any}
        onCancel={handleClose}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <DialogTitle>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center">
              <Typography variant="h6">{isEditMode ? 'Sjabloon bewerken' : 'Nieuw sjabloon'}</Typography>
              <IconButton
                onClick={handleClose}
                size="small">
                <Close />
              </IconButton>
            </Stack>
          </DialogTitle>

          <DialogContent
            sx={{
              overflowY: 'auto',
              pb: 0,
            }}>
            <Stack
              spacing={3}
              sx={{ mt: 1 }}>
              <OvFormInput
                name="name"
                label="Naam"
                type="text"
                rules={{ required: 'Naam is verplicht' }}
                fullWidth
              />

              <OvFormInput
                name="type"
                label="Type"
                type="select"
                options={TEMPLATE_TYPE_OPTIONS}
                valueKey="value"
                labelKey="label"
                output="value"
                rules={{ required: 'Type is verplicht' }}
                fullWidth
              />

              <Box>
                <OvFormInput
                  name="subject"
                  label="Onderwerp"
                  type="text"
                  rules={{ required: 'Onderwerp is verplicht' }}
                  fullWidth
                  inputRef={subjectInputRef}
                  onFocus={() => setActiveField('subject')}
                />
              </Box>

              <Alert
                severity="info"
                sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom>
                  Beschikbare variabelen
                  {activeField && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 1, fontWeight: 'normal' }}>
                      (klik om toe te voegen aan {activeField === 'subject' ? 'onderwerp' : 'bericht'})
                    </Typography>
                  )}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {AVAILABLE_VARIABLES.map(({ variable, description }) => (
                    <Chip
                      key={variable}
                      label={variable}
                      onClick={() => insertVariable(variable)}
                      size="small"
                      color="primary"
                      variant="outlined"
                      title={description}
                      sx={{
                        fontFamily: 'monospace',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'primary.light',
                          color: 'primary.contrastText',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Alert>

              <Box sx={{ position: 'relative' }}>
                <OvFormInput
                  name="body"
                  label="Bericht"
                  type="textarea"
                  rules={{ required: 'Bericht is verplicht' }}
                  minRows={8}
                  fullWidth
                  inputRef={bodyInputRef}
                  onFocus={() => setActiveField('body')}
                  sx={{
                    '& textarea': {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      lineHeight: '1.5',
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    display: 'block',
                    color: 'text.secondary',
                  }}>
                  Variabelen worden weergegeven tussen {'{{'} en {'}}'}. Gebruik de chips hierboven om ze in te voegen.
                </Typography>
              </Box>

              <OvFormInput
                name="is_default"
                label="Als standaard instellen voor dit type"
                type="checkbox"
              />
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'background.paper',
              zIndex: 1,
              justifyContent: 'space-between',
            }}>
            <Box>
              {isEditMode && (
                <Tooltip title="Herstelt het sjabloon naar de oorspronkelijke standaardinhoud">
                  <Button
                    onClick={handleRestoreToDefault}
                    variant="outlined"
                    color="secondary"
                    startIcon={<RestartAlt />}
                    disabled={email.loading}>
                    Herstel naar standaard
                  </Button>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={TOOLTIPS.CANCEL_ESC}>
                <Button onClick={handleClose} variant="outlined">
                  {LABELS.CANCEL}
                </Button>
              </Tooltip>
              <Tooltip title={TOOLTIPS.SAVE_EMAIL_TEMPLATE}>
                <span>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Save />}
                    disabled={email.loading || !form.formState.isValid}>
                    {isEditMode ? 'Bijwerken' : 'Aanmaken'}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </DialogActions>
        </form>
      </OvFormProvider>
    </Dialog>
  )
})
